import { useNavigate, useRouteContext } from '@tanstack/react-router';
import { Alert, Button, Flex, message } from 'antd';
import { useState } from 'react';

import { useCreateBillingPortalSession } from '../../../api/generated/api';
import { formatDate } from '../../../ui/utils/format-date';
import { usePermissions } from '../../account/hooks';
import { formatCredits } from '../shared/billing-format';

import { NotifyAdminButton } from './NotifyAdminButton';

import type { GetAccountResponseDtoMembershipsItemOrganizationCredits } from '../../../api/generated/model/getAccountResponseDtoMembershipsItemOrganizationCredits';
import type { AlertProps } from 'antd';

type Credits = NonNullable<GetAccountResponseDtoMembershipsItemOrganizationCredits>;
type Kind = 'past_due' | 'auto_recharge_declined' | 'exhausted' | 'low';
type Action = 'buy' | 'upgrade' | 'portal';

type Bar = {
  kind: Kind;
  type: AlertProps['type'];
  manager: string;
  member: string;
  action: Action;
  label: string;
};

/**
 * What the organization's state says right now, most urgent first. Nothing is stored: a bar goes
 * away when its state does (a paid renewal, a card fixed and auto-recharge back on, credits added).
 */
const barsFor = (credits: Credits): Bar[] => {
  const bars: Bar[] = [];
  if (credits.pastDue) {
    bars.push({
      kind: 'past_due',
      type: 'error',
      manager: 'Your plan renewal could not be charged. Update your card so your monthly credits are added.',
      member: "Your company's last payment failed. Ask your billing admin to update the card.",
      action: 'portal',
      label: 'Update card',
    });
  }
  if (credits.autoRechargeDeclinedAt && !credits.autoRechargeEnabled) {
    bars.push({
      kind: 'auto_recharge_declined',
      type: 'error',
      manager: 'Auto-recharge is off because the card was declined. Update your card, then turn auto-recharge back on.',
      member: "Your company's auto-recharge is off because a card charge failed. Ask your billing admin.",
      action: 'portal',
      label: 'Update card',
    });
  }
  if (credits.exhausted) {
    bars.push(
      credits.trial
        ? {
            kind: 'exhausted',
            type: 'error',
            manager: 'Your trial credits are used up. Upgrade to the paid plan to keep texting, calling and using AI.',
            member: 'Your company is out of credits. Ask your billing admin.',
            action: 'upgrade',
            label: 'Upgrade now',
          }
        : {
            kind: 'exhausted',
            type: 'error',
            manager: `Your credits are used up. Texting, calling, AI and contact reveals are paused until you buy a top-up${
              credits.renewsAt ? ` or your plan renews on ${formatDate(credits.renewsAt)}` : ''
            }.`,
            member: 'Your company is out of credits. Ask your billing admin.',
            action: 'buy',
            label: 'Buy credits',
          },
    );
  } else if (credits.low) {
    bars.push({
      kind: 'low',
      type: 'warning',
      manager: `You have ${formatCredits(credits.balance)} credits left. Buy a top-up or turn on auto-recharge.`,
      member: 'Your company is running low on credits. Ask your billing admin to add more.',
      action: 'buy',
      label: 'Buy credits',
    });
  }
  return bars;
};

/**
 * The in-app half of the billing notifications (UC-17), from the permission-free credit context.
 * Billing managers get the bar with its action; members get a softer one that names who can fix
 * it, with "Notify admin" when the company is out of credits. Either can hide a bar for this
 * session; it comes back while the state lasts.
 */
export const BillingAlertsBar = () => {
  const skipBillingGate = import.meta.env.VITE_SKIP_BILLING_GATE === 'true';
  const navigate = useNavigate();
  const { selectedOrganization, selectedMembership } = useRouteContext({ from: '/_authenticated/_app' });
  const organizationId = selectedOrganization.id;
  const { can } = usePermissions();
  const canManageBilling = can('settings', 'manageBilling');
  const portalMutation = useCreateBillingPortalSession();
  const [hidden, setHidden] = useState<ReadonlySet<string>>(() => new Set());

  const credits = selectedMembership.organization.credits;
  if (skipBillingGate || !credits) return null;

  const key = (kind: Kind) => `${organizationId}:${kind}`;
  const bars = barsFor(credits).filter((bar) => !hidden.has(key(bar.kind)));
  if (bars.length === 0) return null;

  const hide = (kind: Kind) => setHidden((current) => new Set(current).add(key(kind)));

  const openPortal = async () => {
    try {
      const result = await portalMutation.mutateAsync({ organizationId });
      if (result.data.url) window.location.assign(result.data.url);
    } catch {
      message.error('Failed to open Manage subscription. Please try again.');
    }
  };

  return (
    <Flex vertical>
      {bars.map((bar) =>
        canManageBilling ? (
          <Alert
            key={bar.kind}
            data-testid={`billing-alert-${bar.kind}`}
            banner
            type={bar.type}
            showIcon
            closable={{ onClose: () => hide(bar.kind) }}
            title={bar.manager}
            action={
              <Button
                size="small"
                type="primary"
                data-testid="billing-alert-action"
                loading={bar.action === 'portal' && portalMutation.isPending}
                onClick={() => {
                  if (bar.action === 'portal') void openPortal();
                  else if (bar.action === 'upgrade') void navigate({ to: '/settings/billing', search: { upgrade: 1 } });
                  else void navigate({ to: '/settings/billing', search: { buy: 1 } });
                }}
              >
                {bar.label}
              </Button>
            }
          />
        ) : (
          <Alert
            key={bar.kind}
            data-testid={`billing-alert-${bar.kind}`}
            banner
            type="info"
            showIcon
            closable={{ onClose: () => hide(bar.kind) }}
            title={bar.member}
            action={bar.kind === 'exhausted' ? <NotifyAdminButton organizationId={organizationId} /> : undefined}
          />
        ),
      )}
    </Flex>
  );
};
